import React from 'react';
import ReactDOM from 'react-dom/client';
import { EuiProvider } from '@elastic/eui';
import App from './App';
import { THEME, applyTheme, getSavedThemeMode } from './utils/theme';
import { MODULE_COLORS } from './components/PallasAnalyst/PallasUI';
import { resolveAuthMode } from './authMode';

/**
 * Pallas AI — Iframe-Bridge Entry Point
 *
 * Trusted parent applications may post a real Keycloak access token through
 * the origin-checked bridge. A posted username is display data only and is
 * never accepted as authentication.
 *
 * Direct production access is protected by Keycloak. Athena Core and Ageleia
 * embeds explicitly select the origin-checked parent bridge.
 */

// Authentication is fail-closed in production. Only ?auth=parent may override
// standalone Keycloak SSO. Browser API-key mode has been removed.
// Two modes:
//   keycloak → standalone SSO (DEFAULT — new tab / direct visit; redirects to Keycloak)
//   parent   → postMessage bridge for a real user token; if the parent cannot
//              provide one (Athena Core), the iframe obtains its own Keycloak SSO token
const urlParams = new URLSearchParams(window.location.search);
const AUTH_MODE = resolveAuthMode({
  search: window.location.search,
  configuredMode: process.env.REACT_APP_AUTH_MODE,
  nodeEnv: process.env.NODE_ENV,
  hostname: window.location.hostname,
});

// In-window embed (Ageleia Alert Triage) passes ?chrome=none. Absent for standalone,
// /ai/pallas, and the Wazuh plugin → those keep the navy loader unchanged.
const HIDE_CHROME = urlParams.get('chrome') === 'none';

// ── Theme mode resolution (BEFORE first render) ─────────────────────────────
// Precedence: Ageleia embed → forced dark (its charcoal skin layers on top,
// theme toggle hidden) · saved user preference · default dark. The sidebar
// toggle writes localStorage and reloads, so this runs exactly once per page.
const IS_AGELEIA_EMBED = urlParams.get('theme') === 'ageleia' || urlParams.get('chrome') === 'none';
const THEME_MODE = IS_AGELEIA_EMBED ? 'dark' : getSavedThemeMode();
applyTheme(THEME_MODE);
// Read by PallasNavigationRail (toggle visibility + current mode).
window.__pallasTheme = { mode: THEME_MODE, locked: IS_AGELEIA_EMBED };

// Light surfaces need darker module accents — the dark-tuned mid-tones
// (teal #16c5c0, amber #F59E0B, …) fall under 3:1 contrast on white. Dark
// mode and the Ageleia embed keep the originals. Mutating MODULE_COLORS
// pre-render is the same contract the Ageleia block below uses.
if (THEME_MODE === 'light') {
  MODULE_COLORS.chat.accent      = '#0F9D97';  // teal → deep teal
  MODULE_COLORS.vulns.accent     = '#B45309';  // amber → amber-700
  MODULE_COLORS.standup.accent   = '#7C3AED';  // violet → violet-600
  MODULE_COLORS.narrative.accent = '#0284C7';  // sky → sky-600
  MODULE_COLORS.nids.accent      = '#C2410C';  // orange → orange-700
}

// In-window Ageleia embed (?theme=ageleia): re-skin the shared THEME + narrative
// accent to Ageleia's charcoal/blue BEFORE first render. Gated — standalone Pallas
// and the Wazuh plugin (no ?theme) keep their own sky/silver identity. UI-only:
// mutates color tokens only; no logic or render-path change. Safe because the
// narrative view reads THEME.* / MODULE_COLORS.* at render, not at import.
if (IS_AGELEIA_EMBED) {
  Object.assign(THEME, {
    card:       '#151519',  // flat nav-charcoal panels — avoids the lighter-panel
    cardSubtle: '#151519',  //   "gradient/patchy" look; panels defined by border only
    cardHover:  '#1D1E24',  // gentle hover lift only
    accent:     '#16c5c0',  // silver -> Ageleia cyan
    primary:    '#61a2ff',  // blue-500 -> Ageleia primary
    textMuted:  '#7c8dab',  // lighter muted text (WCAG-AA on dark)
    rowBg:      '#151519',  // alert cards/rows -> Ageleia side-nav charcoal
    rowBgHover: '#1A1B21',  // very subtle hover lift — avoids the washed/'fainted' look
    rowBgMuted: '#151519',  // muted rows (opacity 0.7 already applied by the row)
  });
  MODULE_COLORS.narrative.accent = '#61a2ff';  // for any render-time reads; the frozen ACCENT const is resolved inside PallasAttackNarrative.js (runs before this mutation)
}

// Keycloak realm role required to use Pallas in standalone SSO (keycloak) mode.
const PALLAS_ACCESS_ROLE = 'pallas-access';

// Maximum time we wait for the parent to post a real Keycloak token before
// falling back to Pallas' own Keycloak SSO flow.
const PARENT_AUTH_TIMEOUT_MS = 5000;

// ── Loading Screen ──────────────────────────────────────────────────────────

const LoadingScreen = () => (
  <div style={{
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: HIDE_CHROME ? THEME.bg : THEME.bgDeep,
    color: THEME.textSecondary,
    fontFamily: 'Inter, -apple-system, sans-serif',
    gap: 16,
  }}>
    <div style={{
      width: 24, height: 24, border: `2px solid ${THEME.border}`,
      borderTopColor: MODULE_COLORS.chat.accent, borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: 13 }}>Initializing Pallas AI...</span>
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ── Iframe-Bridge Mode (parent plugin posts pallas-auth-token) ──────────────
//
// We accept a real Keycloak token only from an allowlisted parent origin. A
// legacy Athena Core plugin that posts a username without a token is sent
// through Pallas' own Keycloak SSO flow instead.
//
// SECURITY: origin must match REACT_APP_PARENT_ORIGIN (or one of the CSV).
// Messages from any other origin are silently ignored.

const PARENT_ORIGIN = process.env.REACT_APP_PARENT_ORIGIN || '';
const PARENT_ORIGINS_CSV = process.env.REACT_APP_PARENT_ORIGINS_CSV || '';
const ALLOWED_PARENT_ORIGINS = (() => {
  const set = new Set();
  if (PARENT_ORIGIN) set.add(PARENT_ORIGIN);
  PARENT_ORIGINS_CSV.split(',').map(s => s.trim()).filter(Boolean).forEach(o => set.add(o));
  if (set.size === 0) {
    // Auto-derive the dashboard origin from our own hostname, mirroring the
    // plugin's `pallas.<dashboard-host>` convention:
    //   SPA at  pallas.athena.athenasecuritygrp.com → accept athena.athenasecuritygrp.com
    //   SPA at  pallas.test.athenasecuritygrp.com   → accept test.athenasecuritygrp.com
    //   SPA at  pallas.nbs.athenasecuritygrp.com    → accept nbs.athenasecuritygrp.com
    // Every tenant works with no per-tenant config — DNS is the only thing
    // that has to be set up. Override via REACT_APP_PARENT_ORIGIN(S_CSV) when
    // the deploy doesn't follow the convention (white-label, custom domain).
    const myHost = (typeof window !== 'undefined' && window.location && window.location.hostname) || '';
    if (myHost.indexOf('pallas.') === 0) {
      set.add(`https://${myHost.substring('pallas.'.length)}`);
    }
  }
  return set;
})();

function ParentAuthApp() {
  const [authReady, setAuthReady] = React.useState(!!window.__pallasParentAuth?.token);
  const [useKeycloak, setUseKeycloak] = React.useState(false);

  React.useEffect(() => {
    const handler = (event) => {
      if (!ALLOWED_PARENT_ORIGINS.has(event.origin)) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== 'pallas-auth-token') return;
      if (!data.token) {
        // Athena Core's legacy plugin can resolve a username but cannot prove
        // that identity. Use the tenant Keycloak SSO session instead.
        setUseKeycloak(true);
        return;
      }
      window.__pallasParentAuth = {
        token:        data.token,
        username:     data.username || '',
        displayName:  data.displayName || data.username || '',
        receivedAt:   Date.now(),
        parentOrigin: event.origin,
      };
      setAuthReady(true);
    };
    window.addEventListener('message', handler);

    try {
      window.parent.postMessage({ type: 'pallas-iframe-ready' }, '*');
    } catch (_) { /* sandboxed parent — non-fatal */ }

    const timer = setTimeout(() => {
      if (!window.__pallasParentAuth?.token) setUseKeycloak(true);
    }, PARENT_AUTH_TIMEOUT_MS);

    return () => {
      window.removeEventListener('message', handler);
      clearTimeout(timer);
    };
  }, []);

  if (useKeycloak) return <KeycloakApp />;
  if (!authReady) return <LoadingScreen />;
  return (
    <EuiProvider colorMode={THEME_MODE}>
      <App />
    </EuiProvider>
  );
}

// ── Access Denied Screen (keycloak mode, user lacks pallas-access role) ─────

const PallasAccessDenied = ({ username, onLogout }) => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: HIDE_CHROME ? THEME.bg : THEME.bgDeep,
    fontFamily: 'Inter, -apple-system, sans-serif',
    color: THEME.text,
    padding: 24,
  }}>
    <div style={{
      maxWidth: 480,
      width: '100%',
      backgroundColor: THEME.card,
      borderRadius: 12,
      border: `1px solid ${THEME.border}`,
      padding: 40,
      textAlign: 'center',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
    }}>
      <div style={{
        width: 64, height: 64, borderRadius: '50%',
        backgroundColor: `${THEME.danger}1A`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 24px',
      }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={THEME.danger} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <line x1="12" y1="8" x2="12" y2="12"/>
          <line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      </div>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>Access Denied</h1>
      <p style={{ fontSize: 15, color: THEME.textSecondary, marginBottom: 8, lineHeight: 1.6 }}>
        Your account does not have access to <strong style={{ color: THEME.text }}>Pallas AI</strong>.
      </p>
      <p style={{ fontSize: 13, color: THEME.textMuted, marginBottom: 32, lineHeight: 1.5 }}>
        Contact your administrator to request access.
      </p>
      {username && (
        <div style={{
          backgroundColor: THEME.bgDeep, borderRadius: 8, padding: '12px 16px',
          marginBottom: 24, border: `1px solid ${THEME.border}`, fontSize: 13, color: THEME.textSecondary,
        }}>
          Signed in as <strong style={{ color: THEME.text }}>{username}</strong>
        </div>
      )}
      <button
        onClick={onLogout}
        style={{
          width: '100%', padding: '12px 24px', backgroundColor: MODULE_COLORS.chat.accent,
          color: THEME.onAccent, border: 'none', borderRadius: 8, fontSize: 14,
          fontWeight: 600, cursor: 'pointer',
        }}
        onMouseOver={(e) => e.target.style.backgroundColor = '#0ea5a0'}
        onMouseOut={(e) => e.target.style.backgroundColor = MODULE_COLORS.chat.accent}
      >
        Sign in with a different account
      </button>
    </div>
  </div>
);

// ── Keycloak Mode (standalone SSO — new tab / direct visit) ─────────────────
//
// Wraps the app in ReactKeycloakProvider (onLoad: 'login-required' → redirects
// to Keycloak). After auth, gates on the PALLAS_ACCESS_ROLE realm role. The
// JWT is read by pallasService.authHeaders() when no parent-auth is present.

function KeycloakApp() {
  const { ReactKeycloakProvider } = require('@react-keycloak/web');
  const keycloak = require('./config/keycloak').default;
  const { keycloakInitOptions } = require('./config/keycloak');
  const [denied, setDenied] = React.useState(false);
  const [kcUsername, setKcUsername] = React.useState('');

  const onEvent = React.useCallback((event, error) => {
    if (error) {
      console.error(`[Pallas] Keycloak event ${event}:`, error?.error || error?.message || 'authentication error');
    } else {
      console.info(`[Pallas] Keycloak event: ${event}`);
    }
    if (event === 'onAuthSuccess' || event === 'onReady') {
      if (keycloak.authenticated && keycloak.tokenParsed) {
        const roles = keycloak.tokenParsed.realm_access?.roles || [];
        setKcUsername(keycloak.tokenParsed.preferred_username || '');
        if (!roles.includes(PALLAS_ACCESS_ROLE)) {
          setDenied(true);
        }
      }
    }
  }, [keycloak]);

  const onTokens = React.useCallback((tokens) => {
    if (tokens.token) {
      sessionStorage.setItem('keycloak_token', tokens.token);
    }
  }, []);

  return (
    <>
      <ReactKeycloakProvider
        authClient={keycloak}
        initOptions={keycloakInitOptions}
        LoadingComponent={<LoadingScreen />}
        onEvent={onEvent}
        onTokens={onTokens}
      >
        <EuiProvider colorMode={THEME_MODE}>
          <App />
        </EuiProvider>
      </ReactKeycloakProvider>
      {denied && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99999 }}>
          <PallasAccessDenied
            username={kcUsername}
            onLogout={() => keycloak.logout({ redirectUri: window.location.origin })}
          />
        </div>
      )}
    </>
  );
}

// ── Render ───────────────────────────────────────────────────────────────────

const root = ReactDOM.createRoot(document.getElementById('root'));

if (AUTH_MODE === 'parent') {
  console.info('[Pallas] Auth mode: iframe-bridge (parent postMessage)');
  root.render(<ParentAuthApp />);
} else {
  console.info('[Pallas] Auth mode: Keycloak SSO (standalone)');
  root.render(<KeycloakApp />);
}
